/**
 * useImportTransactions Hook
 * 
 * Hook para importação de lançamentos
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  validateImportFile,
  validateTransactions,
  importTransactions,
} from '../services/import.service';
import type {
  ExportData,
  ImportOptions,
  ImportResult,
  ImportValidationResult,
} from '../../../types/export-import';
import { toast } from 'sonner';

interface UseImportTransactionsOptions {
  companyId: string;
  userId: string;
}

export function useImportTransactions({ companyId, userId }: UseImportTransactionsOptions) {
  const queryClient = useQueryClient();
  const [importProgress, setImportProgress] = useState({
    current: 0,
    total: 0,
    message: '',
  });

  // Mutation: Validar arquivo
  const validateFileMutation = useMutation({
    mutationFn: async (file: File): Promise<ExportData> => {
      return validateImportFile(file);
    },
    onError: (error) => {
      console.error('[useImportTransactions] Validation error:', error);
      toast.error('Erro ao validar arquivo', {
        description: error instanceof Error ? error.message : 'Erro desconhecido',
      });
    },
  });

  // Mutation: Validar transações
  const validateTransactionsMutation = useMutation({
    mutationFn: async ({
      data,
      options,
    }: {
      data: ExportData;
      options: ImportOptions;
    }): Promise<ImportValidationResult> => {
      return validateTransactions(data, companyId, options);
    },
    onError: (error) => {
      console.error('[useImportTransactions] Transaction validation error:', error);
      toast.error('Erro ao validar transações', {
        description: error instanceof Error ? error.message : 'Erro desconhecido',
      });
    },
  });

  // Mutation: Importar transações
  const importMutation = useMutation({
    mutationFn: async ({
      data,
      options,
    }: {
      data: ExportData;
      options: ImportOptions;
    }): Promise<ImportResult> => {
      setImportProgress({ current: 0, total: data.transactions.length, message: 'Iniciando importação...' });
      
      const result = await importTransactions(data, companyId, userId, options, (progress) => {
        setImportProgress(progress);
      });

      return result;
    },
    onSuccess: (result) => {
      // Invalidar queries de transações
      queryClient.invalidateQueries({ queryKey: ['transactions'] });

      // Invalidar dados mestres se foram criados
      if (result.created_master_data) {
        queryClient.invalidateQueries({ queryKey: ['transaction-categories', companyId] });
        queryClient.invalidateQueries({ queryKey: ['payment-methods', companyId] });
        queryClient.invalidateQueries({ queryKey: ['business-partners', companyId] });
        queryClient.invalidateQueries({ queryKey: ['document-sources', companyId] });
        queryClient.invalidateQueries({ queryKey: ['cost-centers', companyId] });
        queryClient.invalidateQueries({ queryKey: ['tags', companyId] });
      }

      // Mostrar resultado
      if (result.success) {
        toast.success('Importação concluída com sucesso!', {
          description: `${result.imported} lançamentos importados${
            result.skipped > 0 ? `, ${result.skipped} ignorados` : ''
          }`,
        });
      } else {
        toast.warning('Importação concluída com erros', {
          description: `${result.imported} importados, ${result.errors} com erro, ${result.skipped} ignorados`,
        });
      }

      setImportProgress({ current: 0, total: 0, message: '' });
    },
    onError: (error) => {
      console.error('[useImportTransactions] Import error:', error);
      toast.error('Erro ao importar lançamentos', {
        description: error instanceof Error ? error.message : 'Erro desconhecido',
      });
      setImportProgress({ current: 0, total: 0, message: '' });
    },
  });

  return {
    // Validação de arquivo
    validateFile: validateFileMutation.mutate,
    isValidatingFile: validateFileMutation.isPending,
    validatedData: validateFileMutation.data,
    fileValidationError: validateFileMutation.error,

    // Validação de transações
    validateTransactions: validateTransactionsMutation.mutate,
    isValidatingTransactions: validateTransactionsMutation.isPending,
    validationResult: validateTransactionsMutation.data,
    transactionValidationError: validateTransactionsMutation.error,

    // Importação
    importTransactions: importMutation.mutate,
    isImporting: importMutation.isPending,
    importResult: importMutation.data,
    importError: importMutation.error,
    importProgress,

    // Reset
    reset: () => {
      validateFileMutation.reset();
      validateTransactionsMutation.reset();
      importMutation.reset();
      setImportProgress({ current: 0, total: 0, message: '' });
    },
  };
}

